/**
 * Queries for the Agent runs panel (#169 slice 3).
 *
 * The panel and a definition read GitLab live (mostly a scan of every open
 * MR's changes), so they have NO timer. The panel loads on open, refetches
 * on every window focus (`'always'`: the app's 60 s staleTime does not hold
 * it back), after a change (the mutations) and from a Refresh button. A
 * definition's detail is the same scan again, so it does NOT refetch on
 * focus by itself: it refetches after a change, from Refresh, and when the
 * panel's focus refetch shows its entry changed (`DefinitionDetail`), so a
 * focus costs one scan, not two.
 * None of the three retries a failed GET: a 403/404/503 answers the same
 * again, after another scan under the viewer's credential; Refresh is the
 * retry. Only a definition's runs poll, every 10 s, while a row is still
 * running or its fix MR's gate is.
 */

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { getDefinition, getDefinitionRuns, getPanel } from '../api/projectPanel';
import { bumpPanelEpoch } from './panelEpoch';
import { isTerminalRunStatus } from '../types/agentRun.types';
import type {
  DefinitionDetail,
  DefinitionRunRow,
  PanelResponse,
} from '../types/projectPanel.types';

export const RUNS_POLL_MS = 10_000;

/** Every key is under `panel(projectId)`, so one prefix covers a project. */
export const projectPanelKeys = {
  panel: (projectId: string): readonly unknown[] => ['projectPanel', projectId],
  definitions: (projectId: string): readonly unknown[] => ['projectPanel', projectId, 'definition'],
  definition: (projectId: string, fileName: string | null): readonly unknown[] => [
    'projectPanel',
    projectId,
    'definition',
    fileName,
  ],
  /** Every definition's runs: Refresh invalidates them with the definitions. */
  allRuns: (projectId: string): readonly unknown[] => ['projectPanel', projectId, 'runs'],
  runs: (projectId: string, name: string | null): readonly unknown[] => [
    'projectPanel',
    projectId,
    'runs',
    name,
  ],
  /** The editor's lint of a text; not under `definitions`, so a change keeps it. */
  preview: (projectId: string, name: string | null, text: string): readonly unknown[] => [
    'projectPanel',
    projectId,
    'preview',
    name,
    text,
  ],
  /** The mutation key every panel write shares (pause, CI wiring, Save, Withdraw). */
  write: (projectId: string): readonly unknown[] => ['projectPanel', projectId, 'write'],
};

export function useProjectPanel(projectId: string): UseQueryResult<PanelResponse> {
  const client = useQueryClient();
  return useQuery({
    queryKey: projectPanelKeys.panel(projectId),
    queryFn: () => {
      bumpPanelEpoch(client, projectId);
      return getPanel(projectId);
    },
    enabled: projectId.length > 0,
    refetchOnWindowFocus: 'always',
    retry: false,
  });
}

/** By the summary's `file_name`; idle while none is selected. */
export function useDefinition(
  projectId: string,
  fileName: string | null,
): UseQueryResult<DefinitionDetail> {
  return useQuery({
    queryKey: projectPanelKeys.definition(projectId, fileName),
    queryFn: () => getDefinition(projectId, fileName ?? ''),
    enabled: projectId.length > 0 && fileName !== null,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** A row still moving: a non-terminal run, or a fix MR whose gate runs. */
export function isRowLive(row: DefinitionRunRow): boolean {
  if (row.gate === 'running') return true;
  return row.kind === 'run' && !isTerminalRunStatus(row.status);
}

/** By the definition's name; idle while none is selected. */
export function useDefinitionRuns(
  projectId: string,
  name: string | null,
): UseQueryResult<DefinitionRunRow[]> {
  return useQuery({
    queryKey: projectPanelKeys.runs(projectId, name),
    queryFn: () => getDefinitionRuns(projectId, name ?? ''),
    enabled: projectId.length > 0 && name !== null,
    refetchInterval: (q) => (q.state.data?.some(isRowLive) ? RUNS_POLL_MS : false),
    retry: false,
  });
}
